import { useEffect, useState } from "react";
import { ArrowRight, BarChart3, Boxes, CalendarDays, Eye, EyeOff, FileText, Lock, Mail, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { api, getErrorMessage } from "@/api";
import { useAuth } from "@/auth";

const logoUrl = "https://customer-assets-jt897jd0.emergentagent.net/job_artist-client-portal/artifacts/3z572eq9_ChatGPT%20Image%2029%20lug%202026%2C%2011_13_35.png";
const REMEMBER_KEY = "explosion_remembered_email";

const FEATURES = [
  { icon: CalendarDays, label: "Gestisci eventi" },
  { icon: Users, label: "Coordina collaboratori" },
  { icon: FileText, label: "Tieni sotto controllo preventivi e fatture" },
  { icon: Boxes, label: "Organizza il magazzino" },
  { icon: BarChart3, label: "Analizza le performance" },
];

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showForgotForm, setShowForgotForm] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const rememberedEmail = window.localStorage.getItem(REMEMBER_KEY);
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const authenticatedUser = await login({ email, password });
      if (rememberMe) window.localStorage.setItem(REMEMBER_KEY, email);
      else window.localStorage.removeItem(REMEMBER_KEY);
      navigate(
        authenticatedUser.force_password_change
          ? "/cambia-password"
          : authenticatedUser.roles?.length > 1
            ? "/scegli-profilo"
            : "/",
      );
    } catch (loginError) {
      setError(loginError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (event) => {
    event.preventDefault();
    setForgotLoading(true);
    setForgotMessage("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotMessage(data.message);
    } catch (forgotError) {
      setForgotMessage(getErrorMessage(forgotError));
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <main className="login-page" data-testid="login-page">
      <section className="login-art" data-testid="login-visual-panel">
        <div className="login-art-wave" aria-hidden="true" />
        <div className="login-brand" data-testid="login-brand">
          <img className="login-brand-logo" src={logoUrl} alt="Explosion Eventi" data-testid="login-logo" />
        </div>
        <div className="login-message">
          <p data-testid="login-eyebrow">GESTIONALE CREATIVO</p>
          <h1 data-testid="login-title">Il ritmo del tuo lavoro,<br /><em>in un solo spazio.</em></h1>
          <ul className="login-feature-list" data-testid="login-feature-list">
            {FEATURES.map(({ icon: Icon, label }) => (
              <li key={label}><Icon size={18} /><span>{label}</span></li>
            ))}
          </ul>
        </div>
        <p className="login-footnote" data-testid="login-footnote">DALL'IDEA ALL'EVENTO,<br />TUTTO SOTTO CONTROLLO.</p>
      </section>
      <section className="login-form-area">
        <div className="login-area-wave" aria-hidden="true" />
        {showForgotForm ? (
          <form className="login-form" onSubmit={handleForgotSubmit} data-testid="forgot-password-form">
            <img className="login-form-logo" src={logoUrl} alt="Explosion Eventi" />
            <div className="login-form-heading">
              <p className="eyebrow">PASSWORD DIMENTICATA</p>
              <h2 data-testid="forgot-password-title">Reimposta la password</h2>
              <p data-testid="forgot-password-description">Inserisci la tua email: ti invieremo un link per scegliere una nuova password.</p>
            </div>
            <label data-testid="forgot-email-label">
              Email
              <span className="login-input-icon-wrap">
                <Mail size={17} className="login-input-icon" />
                <input
                  data-testid="forgot-email-input"
                  type="email"
                  placeholder="Inserisci la tua email"
                  value={forgotEmail}
                  onChange={(event) => setForgotEmail(event.target.value)}
                  required
                  style={{ paddingLeft: 40 }}
                />
              </span>
            </label>
            {forgotMessage && <p className="form-hint" data-testid="forgot-password-message">{forgotMessage}</p>}
            <button data-testid="forgot-submit-button" className="primary-button login-submit-button" disabled={forgotLoading} type="submit">
              {forgotLoading ? "Invio in corso" : "Invia link di reset"}
              <ArrowRight size={18} />
            </button>
            <button
              type="button"
              className="login-forgot-link"
              data-testid="back-to-login-button"
              style={{ justifySelf: "center" }}
              onClick={() => { setShowForgotForm(false); setForgotMessage(""); }}
            >
              Torna al login
            </button>
          </form>
        ) : (
        <form className="login-form" onSubmit={handleSubmit} data-testid="login-form">
          <img className="login-form-logo" src={logoUrl} alt="Explosion Eventi" data-testid="login-form-logo" />
          <div className="login-form-heading">
            <p className="eyebrow" data-testid="login-form-eyebrow">BENTORNATO</p>
            <h2 data-testid="login-form-title">Accedi al tuo gestionale</h2>
            <p data-testid="login-form-description">Gestisci artisti, clienti e preventivi.</p>
          </div>
          <label data-testid="login-email-label">
            Email
            <span className="login-input-icon-wrap">
              <Mail size={17} className="login-input-icon" />
              <input
                data-testid="login-email-input"
                type="email"
                placeholder="Inserisci la tua email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                required
                style={{ paddingLeft: 40 }}
              />
            </span>
          </label>
          <label data-testid="login-password-label">
            Password
            <span className="login-input-icon-wrap">
              <Lock size={17} className="login-input-icon" />
              <input
                data-testid="login-password-input"
                type={showPassword ? "text" : "password"}
                placeholder="Inserisci la tua password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
                style={{ paddingLeft: 40, paddingRight: 44 }}
              />
              <button
                type="button"
                data-testid="login-password-toggle"
                title={showPassword ? "Nascondi password" : "Mostra password"}
                aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                onClick={() => setShowPassword((visible) => !visible)}
                className="login-password-toggle"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </span>
          </label>
          <div className="login-form-options">
            <label className="login-remember-label" data-testid="login-remember-label">
              <input
                type="checkbox"
                data-testid="login-remember-checkbox"
                checked={rememberMe}
                onChange={(event) => setRememberMe(event.target.checked)}
              />
              Ricordami
            </label>
            <button
              type="button"
              className="login-forgot-link"
              data-testid="login-forgot-password"
              onClick={() => { setShowForgotForm(true); setForgotEmail(email); }}
            >
              Password dimenticata?
            </button>
          </div>
          {error && <p className="form-error" data-testid="login-error-message">{error}</p>}
          <button
            data-testid="login-submit-button"
            className="primary-button login-submit-button"
            disabled={loading}
            type="submit"
          >
            {loading ? "Accesso in corso" : "Accedi"}
            <ArrowRight size={18} />
          </button>
          <div className="login-form-footer" data-testid="login-form-footer">
            <strong>Explosion Eventi</strong>
            <span>Gestione Eventi · Artisti · Clienti · Preventivi</span>
          </div>
        </form>
        )}
      </section>
    </main>
  );
}
